import { useState, useEffect, useCallback, useMemo } from 'react';
import { getRouteGeometry } from '../services/osrmService';

// Tempo total da animação em milissegundos (3 segundos)
const ANIMATION_DURATION_MS = 3000;

export function useRondaRoute(events, selectedRonda) {
  const [routePoints, setRoutePoints] = useState([]);
  const [animationIndex, setAnimationIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasCompletedAnimation, setHasCompletedAnimation] = useState(false);

  // Filtrar eventos da ronda seleccionada e ordenar por created_at
  const rondaEvents = useMemo(() => {
    return events
      .filter(e => e.ronda_id === selectedRonda && e.location?.latitude && e.location?.longitude)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [events, selectedRonda]);

  // Extrair rondaInfo do primeiro evento
  const rondaInfo = useMemo(() => {
    if (rondaEvents.length === 0) return null;
    const firstEvent = rondaEvents[0];
    return {
      codigo: firstEvent.ronda_id,
      nome: firstEvent.ronda_name || firstEvent.ronda_id,
      entity_name: firstEvent.entity?.name
    };
  }, [rondaEvents]);

  // Buscar rota quando eventos ou ronda mudam
  useEffect(() => {
    // Filtrar eventos da ronda seleccionada
    const filteredEvents = events
      .filter(e => e.ronda_id === selectedRonda && e.location?.latitude && e.location?.longitude)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    if (!selectedRonda || filteredEvents.length < 2) {
      setRoutePoints([]);
      setAnimationIndex(0);
      setIsAnimating(false);
      setHasCompletedAnimation(false);
      return;
    }

    const fetchRoute = async () => {
      setLoading(true);
      const coords = filteredEvents.map(e => ({
        latitude: e.location.latitude,
        longitude: e.location.longitude
      }));
      const points = await getRouteGeometry(coords);
      setRoutePoints(points);
      // Reset para início apenas quando uma nova rota é carregada
      setAnimationIndex(0);
      setHasCompletedAnimation(false);
      setLoading(false);
    };

    fetchRoute();
  }, [selectedRonda, events]);

  // Auto-play: Iniciar animação automaticamente quando rota é carregada
  useEffect(() => {
    if (routePoints.length > 0 && !loading && !isAnimating) {
      setIsAnimating(true);
      setAnimationIndex(0);
    }
  }, [routePoints.length, loading, isAnimating]);

  // Controles de animação (para uso futuro se necessário)
  const startAnimation = useCallback(() => {
    setAnimationIndex(0);
    setIsAnimating(true);
    setHasCompletedAnimation(false);
  }, []);

  const stopAnimation = useCallback(() => {
    setIsAnimating(false);
  }, []);

  // Encontrar os índices dos routePoints mais próximos de cada evento
  const eventPointIndices = useMemo(() => {
    if (routePoints.length === 0 || rondaEvents.length === 0) {
      return [];
    }

    // Função para calcular distância entre dois pontos
    const distance = (lat1, lng1, lat2, lng2) => {
      const dLat = lat2 - lat1;
      const dLng = lng2 - lng1;
      return Math.sqrt(dLat * dLat + dLng * dLng);
    };

    // Para cada evento, encontrar o ponto da rota mais próximo
    return rondaEvents.map(event => {
      const eventLat = event.location.latitude;
      const eventLng = event.location.longitude;
      
      let closestIndex = 0;
      let minDistance = Infinity;
      
      routePoints.forEach(([lat, lng], index) => {
        const dist = distance(lat, lng, eventLat, eventLng);
        if (dist < minDistance) {
          minDistance = dist;
          closestIndex = index;
        }
      });
      
      return { event, pointIndex: closestIndex };
    });
  }, [routePoints, rondaEvents]);

  // Calcular o índice do ponto da rota mais próximo do último evento
  const lastEventPointIndex = useMemo(() => {
    if (rondaEvents.length === 0 || eventPointIndices.length === 0) {
      return null;
    }
    
    const lastEvent = rondaEvents[rondaEvents.length - 1];
    const lastEventMapping = eventPointIndices.find(
      mapping => mapping.event.id === lastEvent.id
    );
    
    return lastEventMapping ? lastEventMapping.pointIndex : null;
  }, [rondaEvents, eventPointIndices]);

  // Calcular intervalo dinâmico baseado no número de pontos
  // Para manter o tempo total constante (ANIMATION_DURATION_MS)
  const animationInterval = useMemo(() => {
    if (routePoints.length < 2) return 50; // Default se não houver pontos suficientes
    // Intervalo = tempo total / (número de transições)
    // Número de transições = número de pontos - 1
    const interval = ANIMATION_DURATION_MS / (routePoints.length - 1);
    // Garantir um mínimo de 10ms para não ser muito rápido
    return Math.max(10, Math.round(interval));
  }, [routePoints.length]);

  // Loop de animação
  useEffect(() => {
    // Parar quando chegar ao ponto do último evento ou ao fim da rota
    const stopIndex = lastEventPointIndex !== null 
      ? lastEventPointIndex 
      : routePoints.length - 1;
    
    if (!isAnimating || animationIndex >= stopIndex) {
      if (animationIndex >= stopIndex) {
        setIsAnimating(false);
        setHasCompletedAnimation(true);
        // Garantir que o animationIndex fica no stopIndex (não ultrapassa)
        if (animationIndex > stopIndex) {
          setAnimationIndex(stopIndex);
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setAnimationIndex(prev => {
        const nextIndex = prev + 1;
        const maxIndex = lastEventPointIndex !== null 
          ? lastEventPointIndex 
          : routePoints.length - 1;
        // Não ultrapassar o stopIndex
        return nextIndex > maxIndex ? maxIndex : nextIndex;
      });
    }, animationInterval);

    return () => clearTimeout(timer);
  }, [isAnimating, animationIndex, routePoints.length, animationInterval, lastEventPointIndex]);

  // Determinar qual evento a carrinha já passou baseado no animationIndex
  const getLastEventPassed = useCallback((currentIndex) => {
    if (rondaEvents.length === 0) {
      return null;
    }

    // Se está no início, mostrar o primeiro evento
    if (currentIndex === 0) {
      return rondaEvents[0];
    }

    // Ordenar eventos pelos seus pointIndices (ordem na rota)
    const sortedEvents = [...eventPointIndices].sort((a, b) => a.pointIndex - b.pointIndex);
    
    // Encontrar o último evento cujo ponto mais próximo já foi ultrapassado
    let lastEventPassed = rondaEvents[0];
    
    for (const { event, pointIndex } of sortedEvents) {
      // Se o ponto mais próximo deste evento já foi ultrapassado
      if (currentIndex >= pointIndex) {
        lastEventPassed = event;
      } else {
        // Se ainda não chegou a este evento, parar (eventos estão ordenados pela rota)
        break;
      }
    }
    
    return lastEventPassed;
  }, [rondaEvents, eventPointIndices]);

  // Converter currentPosition de [lat, lng] para {latitude, longitude}
  // Incluir informação do último evento que a carrinha já passou
  const currentPosition = useMemo(() => {
    if (routePoints.length === 0) {
      return null;
    }
    
    // Determinar o índice de paragem
    const stopIndex = lastEventPointIndex !== null 
      ? lastEventPointIndex 
      : routePoints.length - 1;
    
    // Se a animação terminou (completou), sempre usar as coordenadas exatas do último evento
    if (hasCompletedAnimation && rondaEvents.length > 0) {
      const lastEvent = rondaEvents[rondaEvents.length - 1];
      return {
        latitude: lastEvent.location.latitude,
        longitude: lastEvent.location.longitude,
        timestamp: lastEvent.created_at,
        local: lastEvent.local || null
      };
    }
    
    // Se chegou ao stopIndex durante a animação, também usar o último evento
    if (animationIndex >= stopIndex && rondaEvents.length > 0) {
      const lastEvent = rondaEvents[rondaEvents.length - 1];
      return {
        latitude: lastEvent.location.latitude,
        longitude: lastEvent.location.longitude,
        timestamp: lastEvent.created_at,
        local: lastEvent.local || null
      };
    }
    
    // Durante a animação, usar o ponto da rota
    if (animationIndex >= routePoints.length) {
      return null;
    }
    
    const [lat, lng] = routePoints[animationIndex];
    
    // Encontrar o último evento que a carrinha já passou
    const lastEventPassed = getLastEventPassed(animationIndex);
    
    return {
      latitude: lat,
      longitude: lng,
      timestamp: lastEventPassed?.created_at,
      local: lastEventPassed?.local || null
    };
  }, [routePoints, animationIndex, getLastEventPassed, lastEventPointIndex, rondaEvents, isAnimating, hasCompletedAnimation]);

  return {
    routePoints,           // Array completo para Polyline [lat, lng]
    currentPosition,       // Posição actual da carrinha {latitude, longitude}
    animationProgress: routePoints.length > 0
      ? (animationIndex / (routePoints.length - 1)) * 100
      : 0,
    isAnimating,
    loading,
    startAnimation,
    stopAnimation,
    rondaEvents,
    rondaInfo
  };
}

